import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Wallet, ArrowDownLeft, ArrowUpRight, Shield, Cpu, Copy, Check } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { StatCard } from '../components/common/StatCard';
import { Pagination } from '../components/common/Pagination';
import { getAddress, getAddressTransactions } from '../lib/api';
import { formatNumber, formatGG, timeAgo, copyToClipboard, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function AddressPage() {
  const { address } = useParams();
  const navigate = useNavigate();
  const [addressData, setAddressData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const limit = 20;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [addrRes, txRes] = await Promise.all([
          getAddress(address),
          getAddressTransactions(address, 1, limit),
        ]);
        setAddressData(addrRes.data);
        setTransactions(txRes.data);
      } catch (err) {
        setError('Address not found or invalid');
        console.error('Error fetching address:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    setCurrentPage(1);
  }, [address]);

  useEffect(() => {
    if (currentPage === 1) return;
    
    const fetchTransactions = async () => {
      setTxLoading(true);
      try {
        const txRes = await getAddressTransactions(address, currentPage, limit);
        setTransactions(txRes.data);
      } catch (err) {
        console.error('Error fetching transactions:', err);
      } finally {
        setTxLoading(false);
      }
    };

    fetchTransactions();
  }, [address, currentPage]);

  const handleCopy = async () => {
    const success = await copyToClipboard(address);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const txColumns = [
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} link={`/tx/${row.hash}`} />,
    },
    {
      header: 'Block',
      cell: (row) => (
        <span 
          className="text-primary hover:underline cursor-pointer font-mono"
          onClick={(e) => { e.stopPropagation(); navigate(`/block/${row.block_height}`); }}
        >
          #{formatNumber(row.block_height, 0)}
        </span>
      ),
    },
    {
      header: 'Direction',
      cell: (row) => {
        const isIncoming = row.to_address === address;
        return (
          <div className="flex items-center gap-2">
            {isIncoming ? (
              <ArrowDownLeft className="w-4 h-4 text-green-400" />
            ) : (
              <ArrowUpRight className="w-4 h-4 text-red-400" />
            )}
            <span className={isIncoming ? 'text-green-400' : 'text-red-400'}>
              {isIncoming ? 'IN' : 'OUT'}
            </span>
          </div>
        );
      },
    },
    {
      header: 'From/To',
      cell: (row) => {
        const isIncoming = row.to_address === address;
        const otherAddress = isIncoming ? row.from_address : row.to_address;
        return <HashDisplay hash={otherAddress} type="address" link={`/address/${otherAddress}`} />;
      },
    },
    {
      header: 'Value',
      cell: (row) => {
        const isIncoming = row.to_address === address;
        return (
          <span className={`font-mono ${isIncoming ? 'text-green-400' : 'text-red-400'}`}>
            {isIncoming ? '+' : '-'}{formatNumber(row.value, 4)} GG
          </span>
        );
      },
    },
    {
      header: 'Time',
      cell: (row) => <span className="text-muted-foreground text-sm">{timeAgo(row.timestamp)}</span>,
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <Skeleton className="h-12 w-full max-w-lg mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 text-center">
          <div className="data-card p-12">
            <Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-2xl font-bold mb-2">Address Not Found</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const totalPages = Math.ceil(addressData.tx_count / limit);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="address-page">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
              <Wallet className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">Address</h1>
              <div className="flex items-center gap-2">
                {addressData.is_validator && (
                  <Badge variant="outline" className="text-green-400 border-green-400/30">
                    <Shield className="w-3 h-3 mr-1" />
                    Validator
                  </Badge>
                )}
                {addressData.is_miner && (
                  <Badge variant="outline" className="text-purple-400 border-purple-400/30">
                    <Cpu className="w-3 h-3 mr-1" />
                    Miner
                  </Badge>
                )}
                {addressData.is_contract && (
                  <Badge variant="outline" className="text-yellow-400 border-yellow-400/30">
                    Contract
                  </Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 p-4 rounded-xl bg-white/5 dark:bg-black/20">
            <code className="font-mono text-sm md:text-base break-all flex-1">{address}</code>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleCopy}
              className="shrink-0"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-400" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Balance"
            value={formatNumber(addressData.balance, 4)}
            subtitle="GG"
            icon={Wallet}
            accentColor="primary"
          />
          <StatCard
            title="Staked"
            value={formatNumber(addressData.staked, 4)}
            subtitle="GG"
            icon={Shield}
            accentColor="success"
          />
          <StatCard
            title="Total Received"
            value={formatNumber(addressData.total_received, 2)}
            subtitle="GG"
            icon={ArrowDownLeft}
            accentColor="secondary"
          />
          <StatCard
            title="Transactions"
            value={formatNumber(addressData.tx_count, 0)}
            subtitle="Total"
            icon={ArrowUpRight}
            accentColor="warning"
          />
        </div>

        {/* Activity Summary */}
        <div className="data-card p-6 mb-8">
          <h2 className="text-lg font-bold mb-4">Activity Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">First Seen</p>
              <p className="font-mono text-sm">{formatDate(addressData.first_seen)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Last Seen</p>
              <p className="font-mono text-sm">{formatDate(addressData.last_seen)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Sent</p>
              <p className="font-mono">{formatNumber(addressData.total_sent, 4)} GG</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Net Flow</p>
              <p className={`font-mono ${addressData.total_received - addressData.total_sent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {addressData.total_received - addressData.total_sent >= 0 ? '+' : ''}
                {formatNumber(addressData.total_received - addressData.total_sent, 4)} GG
              </p>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div>
          <Tabs defaultValue="transactions">
            <TabsList>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
            </TabsList>
            <TabsContent value="transactions">
              <DataTable
                columns={txColumns}
                data={transactions}
                loading={txLoading}
                emptyMessage="No transactions found"
                onRowClick={(row) => navigate(`/tx/${row.hash}`)}
              />
              
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
